import {
  clone,
  concat,
  find,
  first,
  flatMap,
  groupBy,
  includes,
  map,
  max,
  min,
  reduce,
  shuffle,
  size,
  toNumber,
  uniq,
} from 'es-toolkit/compat';
import { defaultMemoize } from 'reselect';
import { oc } from 'ts-optchain';

import { ElaboratedTicketGroup, dashboard } from 'types';
import { LocalDateTime, LocalTime } from 'js-joda';
import { SelectedAvailabilityParams } from 'state/availability';
import {
  SlotsByTicketTypeAndTime,
  SlotsByTime as TimeSlotsByTime,
} from 'selectors/availabilitySelector';
import { TicketGroupsByDateTime } from 'selectors/ticketGroupsByDateTimeSelector';
import { colorToClassName } from 'constants/partyColorConstants';
import { core } from '@tock/coretypes';
import { fastParseDateTime } from 'utils/timeUtils';
import { mostDesirableOverbookingType } from './overbooking';
import { tocktix } from '@tock/protos/dist/types/core';

export enum PacingLimitExceededStatus {
  BLOCKED_VIA_ZERO_PACING = 'BLOCKED_VIA_ZERO_PACING',
  PACING_LIMIT_EXCEEDED = 'PACING_LIMIT_EXCEEDED',
  PACING_LIMIT_NOT_EXCEEDED = 'PACING_LIMIT_NOT_EXCEEDED',
}

/**
 *
 * @param timeSlotKey
 * @param partySize
 * @param pacingIntervals
 * @param originalTicketDateTime
 * @returns true if requested ticketCount exceeds the requested time's available pacing
 */
export function getPacingLimitExceededStatus(
  selectedLocalDateTime: LocalDateTime,
  requestedPartySize: number,
  pacingIntervals: { [k: string]: dashboard.IPacingInterval }
): PacingLimitExceededStatus {
  const requestedTimeSlotKey = selectedLocalDateTime.toLocalTime().toString();

  // If the required values do not exist to check for pacing, return false
  if (!(requestedTimeSlotKey in pacingIntervals)) {
    return PacingLimitExceededStatus.PACING_LIMIT_NOT_EXCEEDED;
  }

  const maxPacing = pacingIntervals[requestedTimeSlotKey].maxPacing ?? undefined;
  const currentPacing = pacingIntervals[requestedTimeSlotKey].currentPacing ?? undefined;

  if (maxPacing === undefined || currentPacing === undefined) {
    return PacingLimitExceededStatus.PACING_LIMIT_NOT_EXCEEDED;
  }

  if (maxPacing === 0) {
    return PacingLimitExceededStatus.BLOCKED_VIA_ZERO_PACING;
  }

  const pacingWithPartySizeChange = currentPacing + requestedPartySize;

  return pacingWithPartySizeChange > maxPacing
    ? PacingLimitExceededStatus.PACING_LIMIT_EXCEEDED
    : PacingLimitExceededStatus.PACING_LIMIT_NOT_EXCEEDED;
}

function ticketGroupMatches(
  ticketGroup: dashboard.TimeSlot,
  ticketTypeId: number[],
  partySize: number
): boolean {
  if (size(ticketTypeId) > 0) {
    return (
      includes(ticketTypeId, ticketGroup.ticketTypeId) &&
      ticketGroupMatchesSize(ticketGroup, partySize)
    );
  }
  return ticketGroupMatchesSize(ticketGroup, partySize);
}

export function getSlotPurchaseSizeFromPartySize(
  ticketGroup: dashboard.TimeSlot | SlotSummary,
  partySize: number
) {
  return Math.ceil(partySize / (ticketGroup.singleUnitQuantity ?? 1));
}

function ticketGroupMatchesSize(ticketGroup: dashboard.TimeSlot, partySize: number) {
  const purchaseSize = getSlotPurchaseSizeFromPartySize(ticketGroup, partySize);
  if (ticketGroup.isEvensOnly && purchaseSize % 2 !== 0) {
    return false;
  }
  return purchaseSize >= ticketGroup.minPurchaseSize && purchaseSize <= ticketGroup.maxPurchaseSize;
}

// Note that the number associated with each of these statuses is important: We merge two statuses (e.g. status
// on two different ticket groups in the same kind) by taking the minimum-valued status. (So, e.g., if one
// ticket group is sold out and another is locked, the merged status is locked instead of sold out since locked
// is a lower-numbered status.)
export enum TicketGroupStatus {
  AVAILABLE = 0, // Ticket group can be purchased with the given type and size
  LOCKED = 1, // Ticket group offers the type and size requested and is not sold out, but too many
  // tickets are locked to purchase the requested number of tickets
  HELD = 2, // Ticket group offers the type and size requested and is not sold out, but too many
  // tickets are locked to purchase the requested number of tickets
  SOLD_OUT = 3, // Ticket group offers the type and size requested, but it is already sold out of it
  STRAGGLER_PROTECTED = 4, // Ticket group normally offers the type and size available and it is not sold out,
  // but it is not offering the given size due to communal table straggler protection
  NOT_OFFERED = 5, // Ticket group does not offer the given type and size at all
}

export function ticketGroupStatusForSize(ticketGroup: dashboard.TimeSlot, size: number) {
  if (!ticketGroupMatchesSize(ticketGroup, size)) {
    return TicketGroupStatus.NOT_OFFERED;
  }
  if (ticketGroup.isCommunal) {
    if (ticketGroup.availableTickets < size) {
      if (ticketGroup.availableTickets + ticketGroup.lockedTickets >= size) {
        return TicketGroupStatus.LOCKED;
      } else {
        return TicketGroupStatus.SOLD_OUT;
      }
    }
    if (!ticketGroup.allowsStragglers && ticketGroup.availableTickets === size + 1) {
      return TicketGroupStatus.STRAGGLER_PROTECTED;
    }
    if (ticketGroup.isEvensOnly && size % 2 !== 0) {
      return TicketGroupStatus.NOT_OFFERED;
    }
    return TicketGroupStatus.AVAILABLE;
  } else {
    return ticketGroupStatus(ticketGroup);
  }
}

export function ticketGroupStatus(ticketGroup: dashboard.TimeSlot) {
  if (ticketGroup.soldTickets !== 0) {
    return TicketGroupStatus.SOLD_OUT;
  }
  if (ticketGroup.heldTickets !== 0) {
    return TicketGroupStatus.HELD;
  }
  if (ticketGroup.lockedTickets !== 0) {
    return TicketGroupStatus.LOCKED;
  }
  return TicketGroupStatus.AVAILABLE;
}

export function ticketGroupOfferedFor(
  ticketGroup: dashboard.TimeSlot,
  ticketTypeId: number[],
  partySize: number
) {
  return (
    ticketGroupMatches(ticketGroup, ticketTypeId, partySize) &&
    ticketGroup.numTickets >= getSlotPurchaseSizeFromPartySize(ticketGroup, partySize)
  );
}

export type ServiceTypeOrExperienceVariety = core.Service.Type | 'EVENT' | 'TTG';
export function isDineInServiceType(
  serviceType: ServiceTypeOrExperienceVariety
): serviceType is core.Service.Type {
  return serviceType !== 'EVENT' && serviceType !== 'TTG';
}

export interface SlotSummary {
  ticketGroupId: number[];
  isOffered: boolean;
  hasAvailability: boolean;
  soldTickets: number;
  soldTicketsExcludedFromPacing: number;
  heldTickets: number;
  lockedTickets: number;
  priceRange: { min: number; max: number };
  reachedPacingLimit: boolean;
  singleUnitQuantity: number;
  ticketTypeId: number;
  hasTable: boolean;
  overbookingType: dashboard.OverbookingType;
  seatingAreaIds: number[];
  services: ServiceTypeOrExperienceVariety[];
  dateTime: string;
}

export interface SlotsByTime {
  [dateTime: string]: SlotSummary;
}

export interface SlotsByTimeAndTicketTypeAndSeatingArea {
  slots: {
    [dateTime: string]: { [ticketTypeId: number]: { [seatingAreaId: number]: SlotSummary } };
  };
  hasOverbookings: boolean;
}

function accumulateByTime(
  ticketTypeId: number[],
  size: number,
  acc: SlotsByTime,
  timeSlot: dashboard.TimeSlot
): SlotsByTime {
  const existing: SlotSummary | undefined = acc[timeSlot.dateTime];
  const matchesExperience = ticketGroupOfferedFor(timeSlot, ticketTypeId, size);
  const isTicketGroupAvailable =
    matchesExperience && ticketGroupStatusForSize(timeSlot, size) === TicketGroupStatus.AVAILABLE;

  acc[timeSlot.dateTime] = getSummary(
    timeSlot,
    size,
    existing,
    matchesExperience,
    isTicketGroupAvailable
  );

  return acc;
}

/** Summarizes an array of TimeSlots objects by dateTime. */
export const ticketTypeSizeDateTimes = defaultMemoize(
  (timeSlots: dashboard.TimeSlot[], ticketTypeId: number[], size: number): SlotsByTime => {
    return reduce(timeSlots, accumulateByTime.bind(null, ticketTypeId, size), {});
  }
);

function accumulateByTimeAndTicketType(
  ticketTypeId: number[],
  partySize: number,
  includeOverbookings: boolean,
  useSeatingAreas: boolean,
  acc: SlotsByTimeAndTicketTypeAndSeatingArea,
  timeSlot: dashboard.TimeSlot
): SlotsByTimeAndTicketTypeAndSeatingArea {
  const seatingAreaId = useSeatingAreas
    ? (timeSlot.seatingAreaId ?? NO_SEATING_AREA_ID)
    : NO_SEATING_AREA_ID;
  const existing: SlotSummary | undefined =
    acc?.slots[timeSlot.dateTime]?.[timeSlot.ticketTypeId]?.[seatingAreaId];
  const matchesExperience = ticketGroupOfferedFor(timeSlot, ticketTypeId, partySize);
  const isTicketGroupAvailable =
    ticketGroupStatusForSize(timeSlot, partySize) === TicketGroupStatus.AVAILABLE;
  const isOverbookedSlot = isOverbookedTimeSlot(timeSlot);

  const isValidOverbookedSlot = matchesExperience && isTicketGroupAvailable && isOverbookedSlot;
  acc.hasOverbookings = acc.hasOverbookings || isValidOverbookedSlot;

  const slotMeetsCriteria =
    matchesExperience && isTicketGroupAvailable && (includeOverbookings || !isOverbookedSlot);
  if (slotMeetsCriteria) {
    if (!acc.slots[timeSlot.dateTime]) {
      acc.slots[timeSlot.dateTime] = {};
    }

    if (!acc.slots[timeSlot.dateTime][timeSlot.ticketTypeId]) {
      acc.slots[timeSlot.dateTime][timeSlot.ticketTypeId] = {};
    }

    acc.slots[timeSlot.dateTime][timeSlot.ticketTypeId][seatingAreaId] = getSummary(
      timeSlot,
      partySize,
      existing,
      matchesExperience,
      isTicketGroupAvailable
    );
  }

  return acc;
}

/**
 * When summarizing the pacing limit for a time+experience we consider the
 * pacing limit reached if all slots that can accommodate the selected party
 * size have reachedPacingLimit set to true.
 *
 * If at any point the value is false that means that there is some room so we
 * don't need to bother checking any additional slots.
 *
 * An undefined existingValue indicates we haven't yet encountered any time slots that can
 * fit the current party size.
 */
function getPacingLimitSummary(
  timeSlot: dashboard.TimeSlot,
  partySize: number,
  existingValue: boolean | undefined
) {
  const isValidSlotForParty =
    partySize >= timeSlot.minPurchaseSize && partySize <= timeSlot.maxPurchaseSize;

  if (existingValue === false || !isValidSlotForParty) {
    return existingValue ?? false;
  }

  return timeSlot.reachedPacingLimit;
}

function getSummary(
  timeSlot: dashboard.TimeSlot,
  size: number,
  existing: SlotSummary | undefined,
  matchesExperience: boolean,
  isTicketGroupAvailable: boolean
): SlotSummary {
  const { hasTable, priceCents, ticketTypeId, seatingAreaId } = timeSlot;

  const summarySeatingAreaIds = existing?.seatingAreaIds ?? [];
  if (!summarySeatingAreaIds.includes(seatingAreaId)) {
    summarySeatingAreaIds.push(seatingAreaId);
  }

  const summaryServices = existing?.services ?? [];
  if (!summaryServices.includes(timeSlot.serviceType)) {
    summaryServices.push(timeSlot.serviceType);
  }

  return {
    ticketGroupId: isTicketGroupAvailable
      ? uniq(concat(oc(existing).ticketGroupId([]), [timeSlot.ticketGroupId]))
      : oc(existing).ticketGroupId([]),
    isOffered: oc(existing).isOffered(false) || matchesExperience,
    hasAvailability: oc(existing).hasAvailability(false) || isTicketGroupAvailable,
    soldTickets: oc(existing).soldTickets(0) + timeSlot.soldTickets,
    soldTicketsExcludedFromPacing:
      oc(existing).soldTicketsExcludedFromPacing(0) + timeSlot.soldTicketsExcludedFromPacing,
    heldTickets: oc(existing).heldTickets(0) + timeSlot.heldTickets,
    lockedTickets: oc(existing).lockedTickets(0) + timeSlot.lockedTickets,
    priceRange: matchesExperience
      ? {
          min: min([oc(existing).priceRange.min(priceCents), priceCents]) as number,
          max: max([oc(existing).priceRange.max(priceCents), priceCents]) as number,
        }
      : oc(existing).priceRange({ min: 0, max: 0 }),
    singleUnitQuantity: timeSlot.singleUnitQuantity ?? 1,
    reachedPacingLimit: getPacingLimitSummary(timeSlot, size, existing?.reachedPacingLimit),
    ticketTypeId,
    hasTable,
    overbookingType: mostDesirableOverbookingType(
      timeSlot.overbookingType,
      existing?.overbookingType
    ),
    seatingAreaIds: summarySeatingAreaIds,
    services: summaryServices,
    dateTime: timeSlot.dateTime,
  };
}

export const getSlotsByTicketTypeAndTimeOptions = defaultMemoize(
  (slots: dashboard.TimeSlot[] | undefined): SlotsByTicketTypeAndTime => {
    const slotsByTicketType: SlotsByTicketTypeAndTime = {};

    if (!slots) {
      return slotsByTicketType;
    }

    slots.forEach((timeSlot) => {
      const { ticketTypeId, dateTime } = timeSlot;

      if (ticketTypeId && dateTime) {
        const timeSlotKey = LocalDateTime.parse(dateTime).toLocalTime().toString();

        slotsByTicketType[ticketTypeId] = slotsByTicketType[ticketTypeId] ?? {};
        slotsByTicketType[ticketTypeId][timeSlotKey] =
          slotsByTicketType[ticketTypeId][timeSlotKey] ?? [];

        slotsByTicketType[ticketTypeId][timeSlotKey].push(timeSlot);
      }
    });

    return slotsByTicketType;
  }
);

export const NO_SEATING_AREA_ID = 0;

export const getSlotSummariesByTimeAndTicketType = defaultMemoize(
  (
    timeSlots: dashboard.TimeSlot[],
    partySize: number,
    ticketTypeId: number[] | undefined,
    includeOverbookings: boolean,
    useSeatingAreas: boolean
  ): SlotsByTimeAndTicketTypeAndSeatingArea => {
    return reduce(
      timeSlots,
      accumulateByTimeAndTicketType.bind(
        null,
        ticketTypeId ?? [],
        partySize,
        includeOverbookings,
        useSeatingAreas
      ),
      {
        slots: {},
        hasOverbookings: false,
      }
    );
  }
);

/** Returns time slots grouped by Ticket Group ID. The time slots set for each ticket group ID are sorted by time */
export const getSortedTimeSlotsByTicketGroup = defaultMemoize(
  (timeSlots: dashboard.TimeSlot[]): Record<string, dashboard.TimeSlot[]> => {
    const timeSlotsByTicketGroup = groupBy(timeSlots, 'ticketGroupId');

    // sort
    Object.keys(timeSlotsByTicketGroup).forEach((ticketGroupId) =>
      timeSlotsByTicketGroup[ticketGroupId].sort((timeSlotA, timeSlotB) => {
        const dateTimeA = fastParseDateTime(timeSlotA.dateTime);
        const dateTimeB = fastParseDateTime(timeSlotB.dateTime);
        return dateTimeA.isBefore(dateTimeB) ? -1 : 1;
      })
    );

    return timeSlotsByTicketGroup;
  }
);

// Sizes that are offered on a list of experiences.
export const ticketTypeOfferedSizes = defaultMemoize(
  (ticketGroups: dashboard.TimeSlot[], ticketTypeId?: number[]): number[] => {
    const sizes = map(
      Object.keys(
        reduce(
          ticketGroups,
          (acc: SizeMap, tg) => {
            if (size(ticketTypeId) > 0 && !includes(ticketTypeId, tg.ticketTypeId)) {
              return acc;
            }
            getAvailablePartySizes(
              tg.minPurchaseSize,
              tg.maxPurchaseSize,
              tg.singleUnitQuantity
            ).forEach((size) => (acc[size] = true));
            return acc;
          },
          {}
        )
      ),
      toNumber
    );

    // `keys` auto-sorting makes me nervous
    sizes.sort((sizeA, sizeB) => sizeA - sizeB);

    return sizes;
  }
);

export function getAvailablePartySizes(
  minPurchaseSize: number,
  maxPurchaseSize: number,
  singleUnitQuantity?: number
): Set<number> {
  const sizes = new Set<number>();
  for (let purchaseSize = minPurchaseSize; purchaseSize <= maxPurchaseSize; ++purchaseSize) {
    for (let unitPerPurchase = 1; unitPerPurchase <= (singleUnitQuantity ?? 1); ++unitPerPurchase) {
      sizes.add(purchaseSize * unitPerPurchase);
    }
  }
  return sizes;
}

/**
 * @param ticketTypeId A ticket type id to map to a color
 * Returns the background color style className for a given ticket type id
 */
export const getTicketTypeIdColorClassName = (ticketTypeId: number): string => {
  const colorClassNameList = Object.values(colorToClassName);
  return colorClassNameList[ticketTypeId % colorClassNameList.length].textColor;
};

export const getRandomTicketGroupFromAvailability = (
  availability: SlotSummary,
  ticketGroupsByDateTime: TicketGroupsByDateTime
): ElaboratedTicketGroup | undefined => {
  const ticketGroupId = first(shuffle(clone(availability.ticketGroupId)));

  return find(
    flatMap(ticketGroupsByDateTime, (ticketGroups) => ticketGroups),
    (ticketGroup: ElaboratedTicketGroup) => ticketGroup.id === ticketGroupId
  );
};

export function validateAvailabilityForSelectedFields(
  slotsSummary: SlotsByTicketTypeAndTime,
  ticketTypeId: number,
  localTime: string,
  partySize: number
) {
  if (!slotsSummary[ticketTypeId]?.[localTime]) {
    return false;
  }

  const hasTimeSlotWhereTicketGroupMatchesPartySize = slotsSummary[ticketTypeId][localTime].some(
    (slot) => {
      if (!ticketGroupMatchesSize(slot, partySize)) {
        // Calculating whether exceedExperienceTicketAvailability is actually due to pacing (in which case adding another error message is redundant) or due to something else.
        // The reason this is necessary is because maxPurchaseSize is reduced if pacing is exceeded, and this value is used in ticketGroupMatchesSize.
        const totalTickets = slot.numTickets;
        const unavailableTickets = slot.lockedTickets + slot.soldTickets + slot.heldTickets;
        const actualAvailableTickets = totalTickets - unavailableTickets;

        return actualAvailableTickets > slot.availableTickets;
      }

      return true;
    }
  );

  return hasTimeSlotWhereTicketGroupMatchesPartySize;
}

export function findAllAvailabilityForSelectedFields(
  slotsSummary: SlotsByTicketTypeAndTime,
  ticketTypeId: number,
  localTime: string,
  partySize: number
) {
  return findAllSlotsMatchingPurchaseSize(slotsSummary[ticketTypeId]?.[localTime] ?? [], partySize);
}
export function findAllMatchingSlotsForSingleTicketTypeAndPartySize(
  slotsSummary: SlotsByTicketTypeAndTime,
  ticketTypeId: number,
  partySize: number
): TimeSlotsByTime {
  return Object.entries(slotsSummary[ticketTypeId]).reduce<TimeSlotsByTime>(
    (slotsByTime, [localTime, slots]) => {
      const matchingSlots = findAllSlotsMatchingPurchaseSize(slots, partySize);
      if (matchingSlots.length > 0) {
        slotsByTime[localTime] = matchingSlots;
      }

      return slotsByTime;
    },
    {}
  );
}

export function findAllSlotsMatchingPurchaseSize(
  slots: dashboard.TimeSlot[] | undefined,
  partySize: number
) {
  if (!slots) {
    return [];
  }

  return slots.reduce<dashboard.TimeSlot[]>((matchingSlots, slot) => {
    if (ticketGroupMatchesSize(slot, partySize)) {
      matchingSlots.push(slot);
    }
    return matchingSlots;
  }, []);
}

export function findAllExperienceSlotsMatchingService(
  slotsOnExperience: SlotsByTicketTypeAndTime,
  selectedTicketTypeId: number,
  service: tocktix.messages.Service.Type
): dashboard.TimeSlot[] {
  if (
    !slotsOnExperience ||
    Object.keys(slotsOnExperience).length < 1 ||
    !slotsOnExperience[selectedTicketTypeId] ||
    Object.keys(slotsOnExperience[selectedTicketTypeId]).length < 1
  ) {
    return [];
  }

  const timeKeys = Object.keys(slotsOnExperience[selectedTicketTypeId]);
  return timeKeys
    .flatMap((timeKey) => slotsOnExperience[selectedTicketTypeId][timeKey])
    .filter((slot) => slot.serviceType === service);
}

export function findAllSlotsMatchingService(
  slots: dashboard.TimeSlot[] | undefined,
  service: tocktix.messages.Service.Type | undefined
) {
  if (!slots) {
    return [];
  }

  if (!service) {
    return slots;
  }

  return slots.filter((slot) => slot.serviceType === service);
}

export function findAllSlotsForTicketTypeAndTime(
  slotsSummary: SlotsByTicketTypeAndTime,
  ticketTypeId: number,
  reservationTime: LocalTime
) {
  if (!(ticketTypeId in slotsSummary)) {
    return undefined;
  }

  const slotsByTime = slotsSummary[ticketTypeId];

  return slotsByTime[reservationTime.toString()];
}

export function isOverbookedTimeSlot(slot: dashboard.ITimeSlot | SlotSummary): boolean {
  // .overbookingType shouldn't ever be `undefined`, but you never know with protobuf typecasting...
  return slot.overbookingType !== 'NO_OVERBOOKING' && slot.overbookingType !== undefined;
}

export function isUnassignedOverbookedPurchase(
  availabilityParams: SelectedAvailabilityParams,
  purchase: core.IPurchaseOrder
) {
  return (
    !!availabilityParams.overbookingType &&
    availabilityParams.overbookingType !== 'NO_OVERBOOKING' &&
    (purchase.tableId?.length ?? 0) === 0
  );
}
